import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './select-invoice-modal.scss';
import { SwipeableDrawer, Box } from '@mui/material';
import { ReactComponent as SearchIcon } from '@zonofi/svg-icons/search-icon.svg';
import {
  dispatch,
  getPaymentCustomerInfo,
  getSearchKey,
  getSelectedItems,
  getWorkspaceId,
  Invoice,
  updateSearchKey,
  updateSelectedItemsMap,
  useAppSelector,
  useLazyGetInvoiceListQuery,
} from '@zonofi/zono-money-store';
import { isEmpty } from 'lodash';
import { SortDirection } from '../../constants';
import { useDebounceSearch, usePaginatedList } from '../../hooks';
import { TouchableOpacity, View } from '../../common';
import {
  CommonButton,
  EmptyStateView,
  EndListView,
  ListSkeleton,
  SpinnerLoader,
} from '../../components';
import { InvoiceCard } from '../../invoice';
import { SVGS } from '../../assets';

interface SelectInvoicesModalProps {
  open: boolean;
  onClose: () => void;
}

export const SelectInvoicesModal: React.FC<SelectInvoicesModalProps> = ({
  open,
  onClose,
}) => {
  const selctedInvoiceItems = useAppSelector(getSelectedItems);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const searchKey = useAppSelector(getSearchKey);
  const paymentCustomerInfo = useAppSelector(getPaymentCustomerInfo);

  const [fetchInvoicesApi, invoicesApiResponse] = useLazyGetInvoiceListQuery();
  const { debouncedSearchValue } = useDebounceSearch();
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (invoicesApiResponse.isSuccess && isSearching) {
      setIsSearching(false);
    }
  }, [invoicesApiResponse]);

  const fetchInvoices = useCallback(
    (params: { pageNo: number; pageSize: number }) => {
      fetchInvoicesApi({
        workspaceId: sellerWorkspaceId,
        sortBy: 'invoiceDate',
        sortDirection: SortDirection.DESCENDING,
        dispatchFilter: false,
        inviteId: paymentCustomerInfo?.inviteId,
        ...params,
        ...(debouncedSearchValue ? { searchKey: debouncedSearchValue } : {}),
      });
    },
    [debouncedSearchValue]
  );

  const memoizedInvoiceData = useMemo(() => {
    return (
      invoicesApiResponse?.data?.invoices?.filter(
        (invoice) =>
          invoice.invoiceStatus === 'PP' || invoice.invoiceStatus === 'P'
      ) ?? []
    );
  }, [invoicesApiResponse?.data?.invoices]);

  const {
    data: invoiceData,
    pageNo,
    lastItemRef,
    onScroll,
    isEndOfList,
  } = usePaginatedList<Invoice>({
    fetchDataApiCall: fetchInvoices,
    apiResponseData: memoizedInvoiceData,
    isLoading: invoicesApiResponse.isLoading,
    uniqueKey: 'id',
    dependencies: [debouncedSearchValue],
    isError: invoicesApiResponse.isError,
    paginationMeta: {
      startRecord: invoicesApiResponse?.data?.startRecord || 0,
      endRecord: invoicesApiResponse?.data?.endRecord || 0,
      totalCount: invoicesApiResponse?.data?.totalInvoices || 0,
    },
  });

  const onInvoiceItemPress = (item: Invoice) => {
    dispatch(updateSelectedItemsMap(item));
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      className="invoice-drawer"
    >
      <Box className="invoice-drawer-container">
        <div className="invoice-header-contaier">
          <div className="invoice-header-view">
            <span className="invoice-header-view--title font-semibold">
              {'Select Invoices'}
            </span>
            <TouchableOpacity onPress={onClose}>
              <span className="invoice-header-view--close font-semibold">
                {'Close'}
              </span>
            </TouchableOpacity>
          </div>

          <div className="invoice-drawer-search">
            <SearchIcon />
            <div className="invoice-drawer-search-divider" />
            <input
              type="text"
              placeholder={'Search with invoice number'}
              className="invoice-drawer-search-input font-regular"
              onChange={(e) => dispatch(updateSearchKey(e.target.value))}
              value={searchKey}
              autoFocus
            />
          </div>
        </div>
        <div className="invoice-scroll-container" onScroll={onScroll}>
          {pageNo === 1 &&
          (invoicesApiResponse.isUninitialized ||
            invoicesApiResponse.isFetching ||
            invoicesApiResponse.isLoading ||
            isSearching) ? (
            <ListSkeleton />
          ) : (
            <View className="invoice-list-container">
              {isEmpty(invoiceData) ? (
                <EmptyStateView
                  imageSrc={SVGS.NoInvoicesFound}
                  message={'No Invoice Found'}
                />
              ) : (
                <>
                  <div className="invoice-list-container--heading font-regular">
                    {
                      'Select the below pending invoices to link to the collect payment '
                    }
                  </div>
                  {invoiceData?.map((invoiceItem: Invoice) => (
                    <InvoiceCard
                      key={invoiceItem.id}
                      invoiceItem={invoiceItem}
                      onInvoiceItemPress={onInvoiceItemPress}
                      showSelection
                      showShare={false}
                    />
                  ))}

                  <div ref={lastItemRef} />

                  {!isEndOfList && <SpinnerLoader />}
                  {isEndOfList && <EndListView />}
                </>
              )}
            </View>
          )}
        </div>

        {selctedInvoiceItems.length > 0 && (
          <View className="invoice-list-button-view">
            <CommonButton
              text="Continue"
              onPress={onClose}
              spinnerStyle="invoice-list-button-view-spinner"
              loading={false}
              spinnerColor={'white'}
            />
          </View>
        )}
      </Box>
    </SwipeableDrawer>
  );
};
